// The device status string, and how many channels it carries.
//
//     [<UniqueNumber>]<v1>,<v2>,…,<vN>#<source>
//     [083A8D406BE4]1,0,1,0#BLE
//
// The SAME format on MQTT and BLE — one transport does not get its own dialect.
//
// ⚠️ CHANNEL COUNT IS PER PRODUCT. The spec says in bold: parse dynamically,
// never hardcode 4. Two shapes break a naive split:
//   • DeviceTypes 53/55/60 use `1-<v>` — a DASH, not a comma list
//   • 7+1 and 7+2 end in FAN SPEEDS, which are 10/20/…/100, not booleans, so
//     treating them as on/off makes a fan at speed 20 read as "on" and a fan at
//     10 (off) read as… also on, since any non-zero is truthy

// DeviceType (= DeviceID on the backend record) -> channel count.
export const CHANNELS_BY_TYPE = {
  53: 1, 55: 1, 60: 1,   // 1-touch / fan
  56: 2,                 // 2-touch
  91: 3,                 // Dices Magnet
  49: 4,                 // 4-touch
  1: 5, 92: 5,           // 4+1
  93: 8,                 // 7+1  — 7 switches + a socket/fan level on channel 8
  87: 9,                 // 7+2 Glass Touch — last 2 are fan speeds
};

// Types whose status is `1-<v>` rather than a comma list.
export const DASH_FORMAT_TYPES = [53, 55, 60];

// How many of the trailing values are fan speeds rather than booleans.
export const FAN_CHANNELS_BY_TYPE = { 93: 1, 87: 2 };

/**
 * Panels with a MASTER button, and how many.
 *
 * ⚠️ A MASTER IS NOT A CHANNEL, and believing it is costs you the channel it
 * gets confused with. The 7+1's glass carries eight touch positions — seven
 * switches and, bottom-right, a master — but its eight OUTPUTS are the seven
 * switches plus a dimmable socket. The socket is channel 8; the master is not
 * wired to an output at all.
 *
 * The firmware is explicit. `tuya_mcu.c` keeps `s_master` in its own variable,
 * and `tuya_mcu_build_status` emits only `s_switch[0..count-1]`, so the master
 * NEVER APPEARS IN A STATUS FRAME. Channel 8 is `slot = 7 + fan`, holding
 * FAN_MAP[m] or 10 — which is why the live 7+1 reads 10 there with everything
 * off, and why treating it as the master would have replaced the socket control
 * with a button and left the socket unreachable.
 *
 * ── What the button does ───────────────────────────────────────────────────
 * Confirmed against the hardware: it is ONE button behaving as a toggle over
 * the whole panel — any switch on, it turns everything off; everything already
 * off, it turns everything on. So it sends `allon`/`alloff`, which both firmware
 * generations parse, rather than the Tuya-only `13-1`/`13-0` (DP 13) that would
 * be silently ignored by the ESP8266 units already in the field.
 *
 * Its own state is never reported, so it is drawn from the switches.
 *
 * ⚠️ AND THE DP NUMBER IS NOT EVEN CONSTANT ACROSS PRODUCTS — a second, harder
 * reason not to send it. Read off the Tuya product definitions:
 *
 *     1 / 2 / 4 SWITCH, 30A     master = DP 13
 *     4 SWITCH + 1 FAN          master = DP 103
 *     1 / 2 CURTAIN             no master DP at all
 *
 * So `13-1` is not merely ESP8266-incompatible, it is addressed to the wrong
 * data point on the 4+1 and to nothing at all on a curtain. allon/alloff has
 * neither problem. Full DP maps in docs/11-tuya-mcu-protocol.md.
 */
export const MASTER_CHANNELS_BY_TYPE = { 93: 1 };

export const masterChannelsFor = (deviceType) =>
  MASTER_CHANNELS_BY_TYPE[Number(deviceType)] ?? 0;

// Fan speed ladder for a fan INSIDE A PANEL: 10 is OFF, then eight running
// speeds ending at 100.
//
// ⚠️ THIS IS THE HARDWARE'S LADDER, AND IT IS NOT THE ONE IN THE FIRMWARE
// SOURCE. tuya_mcu.c still reads
//
//     static const int FAN_MAP[9] = {10, 20, 30, 40, 50, 60, 70, 80, 100};
//
// which has a 20 and no 90. Confirmed against the hardware, the real ladder
// SKIPS 20 AND GOES TO 90 — so the count is the same nine (off plus eight) but
// the second entry differs. The hardware wins; the map in that C file is stale.
//
// The consequence if a device ever does report 20: it matches no step, so the
// panel shows nothing selected. That is the visible failure to look for if this
// ever needs revisiting, and it is the reason the ladder is written down in one
// place rather than inferred.
//
// core/devices/fan.js carries a DIFFERENT, six-step ladder ({10,20,40,60,80,
// 100}) inherited from the Flutter fan screen. It is not wrong there — it is
// the STANDALONE fan, a different hardware generation on channel 1 — and
// core/wall/wallButtons.fanStepsFor picks between the two by channel. Using the
// six-step one for a panel fan leaves most of this ladder unreachable.
export const FAN_OFF = 10;
export const FAN_SPEEDS = [30, 40, 50, 60, 70, 80, 90, 100];

// Off plus the running speeds — the full ladder, in order, as the device reports.
export const FAN_LADDER = [FAN_OFF, ...FAN_SPEEDS];

/** Where a reported speed sits on the ladder, 0..1. Off is 0. */
export function fanFractionOf(value) {
  const i = FAN_LADDER.indexOf(Number(value));
  if (i <= 0) return 0;
  return i / (FAN_LADDER.length - 1);
}

/** "Off", "1".."7", "Max" — the caption under the hub. */
export function fanStepLabelOf(value) {
  const i = FAN_LADDER.indexOf(Number(value));
  if (i <= 0) return 'Off';
  if (i >= FAN_LADDER.length - 1) return 'Max';
  return String(i);
}

/** The word for a speed, matching the fan screen's vocabulary. */
export function fanStatusLabelOf(value) {
  const v = Number(value);
  if (!(v > FAN_OFF)) return 'OFF';
  if (v <= 20) return 'Very Low';
  if (v <= 40) return 'Low';
  if (v <= 60) return 'Medium';
  if (v <= 80) return 'High';
  return 'MAX';
}

export const channelsFor = (deviceType) => CHANNELS_BY_TYPE[Number(deviceType)] ?? null;
export const usesDashFormat = (deviceType) => DASH_FORMAT_TYPES.includes(Number(deviceType));
export const fanChannelsFor = (deviceType) => FAN_CHANNELS_BY_TYPE[Number(deviceType)] ?? 0;
/**
 * Channels that are plain on/off loads — the total less the fan/level trailers.
 *
 * This is how many ordinary tiles a panel draws, as opposed to `channelsFor`,
 * which is the width of the status frame. Masters are NOT subtracted: a master
 * is not one of the channels to begin with.
 */
export const switchChannelsFor = (deviceType) => {
  const total = channelsFor(deviceType);
  if (total == null) return null;
  return Math.max(0, total - fanChannelsFor(deviceType));
};

/**
 * What a master press should send, given the loads it governs.
 *
 * Any one on -> everything off. All off -> everything on.
 */
export const masterCommandFor = (switchStates) =>
  ((switchStates || []).some(Boolean) ? 'alloff' : 'allon');


/**
 * Parse a status string.
 *
 * -> { uniqueNumber, values: number[], source, raw } or null.
 *
 * `values` are the raw numbers, NOT booleans — a fan channel's 40 means speed
 * 40, and flattening that to `true` loses the only information it carries.
 * Use isOn()/fanSpeed() to interpret a channel in the light of its DeviceType.
 */
export function parseStatus(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return null;

  const open = text.indexOf('[');
  const close = text.indexOf(']');
  if (open === -1 || close === -1 || close < open) return null;

  const uniqueNumber = text.slice(open + 1, close);
  let body = text.slice(close + 1);

  // `#<source>` — absent on some frames, so its absence is not a parse failure.
  let source = '';
  const hash = body.indexOf('#');
  if (hash !== -1) {
    source = body.slice(hash + 1).trim();
    body = body.slice(0, hash);
  }
  body = body.trim();
  if (!body) return null;

  // `1-<v>` (single channel) vs `a,b,c`. Detected from the STRING rather than
  // from the DeviceType, so a frame parses correctly even when the type is not
  // to hand — the two shapes cannot be confused.
  let values;
  const dash = /^(\d+)-(\d+)$/.exec(body);
  if (dash) {
    values = [Number(dash[2])];
  } else {
    values = body.split(',').map((v) => Number(String(v).trim()));
  }
  if (values.some((v) => !Number.isFinite(v))) return null;

  return { uniqueNumber, values, source, raw: text };
}

/** Whether channel `i` is a fan speed rather than an on/off switch. */
export function isFanChannel(deviceType, index, totalChannels) {
  const fans = fanChannelsFor(deviceType);
  if (!fans) return false;
  const total = totalChannels ?? channelsFor(deviceType) ?? 0;
  return index >= total - fans;
}

/** On/off for a switch channel — and for a fan, "not at the off step". */
export function isOn(value, deviceType, index, totalChannels) {
  if (isFanChannel(deviceType, index, totalChannels)) return Number(value) > FAN_OFF;
  return Number(value) === 1;
}

/** A fan channel's speed, or null when the channel is not a fan. */
export function fanSpeed(value, deviceType, index, totalChannels) {
  if (!isFanChannel(deviceType, index, totalChannels)) return null;
  const v = Number(value);
  return v === FAN_OFF ? 0 : v;
}

// ── Who caused the change (§4) ─────────────────────────────────────────────
// `#Touch` is how a real press is told apart from an echo of our own command —
// which is what keeps optimistic UI honest.
export const SOURCE_TOUCH = 'Touch';
export const SOURCE_BLE = 'BLE';
export const SOURCE_BOOT = 'Boot';

export const isPhysicalPress = (source) =>
  String(source || '').toLowerCase() === SOURCE_TOUCH.toLowerCase();
